import { Link } from 'react-router-dom'
import type { FreshnessStatus } from '../lib/api'
import { useFreshnessOverview } from '../lib/query/freshness'

/**
 * Compact top-bar badge showing whether AdBetter's data is currently fresh.
 *
 * The colour pill is the single thing the operator looks at on login:
 *   - green:  every catalogued feed is up to date
 *   - yellow: at least one feed is stale within its noisy window
 *   - red:    at least one feed is critically stale
 *   - grey:   no checks have run yet (very brief, right after startup)
 *
 * Click → /freshness for the per-job breakdown. Reads the shared
 * `/api/freshness` query (lib/query/freshness.ts) — one 60 s poll for the
 * whole app, so a fresh BQ landing flips the badge to green without a reload
 * and mounting this badge adds no request of its own (UI overhaul PR-2).
 */
export default function FreshnessBadge() {
  const query = useFreshnessOverview()
  const data = query.data ?? null
  const error = query.isError ? query.error.message : null

  const overall = data?.overall.status as FreshnessStatus | undefined
  const counts = data?.overall.counts
  const dotClass = badgeColor(overall, !!error)
  const label = badgeLabel(overall, !!error)

  // Tooltip: spell out the count breakdown + the worst-offending feed by age.
  const worst = data?.jobs
    .filter(j => j.status === 'red' || j.status === 'yellow')
    .sort((a, b) => (b.age_hours ?? 0) - (a.age_hours ?? 0))[0]
  const tooltip = error
    ? `Freshness lookup failed: ${error}`
    : counts
      ? `Data feeds: ${counts.green ?? 0} green · ${counts.yellow ?? 0} yellow · ${counts.red ?? 0} red`
        + (worst
          ? ` — oldest: ${worst.display_name} ${formatHours(worst.age_hours)}`
          : '')
      : 'Checking data freshness…'

  return (
    <Link
      to="/ops?tab=system"
      className="flex items-center gap-1.5 px-2 py-1 border border-gray-200 rounded bg-white text-xs hover:bg-gray-50"
      title={tooltip}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
      <span className="text-gray-700">{label}</span>
      {counts && (counts.red ?? 0) + (counts.yellow ?? 0) > 0 && (
        <span className="text-gray-400">
          · {(counts.red ?? 0) + (counts.yellow ?? 0)} stale
        </span>
      )}
    </Link>
  )
}

function badgeColor(status: FreshnessStatus | undefined, hasError: boolean): string {
  if (hasError) return 'bg-rose-500'
  switch (status) {
    case 'green':    return 'bg-green-500'
    case 'yellow':   return 'bg-amber-400'
    case 'red':      return 'bg-red-500'
    case 'error':    return 'bg-rose-500'
    case 'unknown':  return 'bg-gray-300'
    default:         return 'bg-gray-300'
  }
}

function badgeLabel(status: FreshnessStatus | undefined, hasError: boolean): string {
  if (hasError) return 'Data: ?'
  switch (status) {
    case 'green':    return 'Data: current'
    case 'yellow':   return 'Data: stale'
    case 'red':      return 'Data: critical'
    case 'error':    return 'Data: error'
    case 'unknown':  return 'Data: ?'
    default:         return 'Data: ?'
  }
}

function formatHours(h: number | null): string {
  if (h == null) return '—'
  if (h < 1) return `${Math.round(h * 60)}m ago`
  if (h < 48) return `${h.toFixed(1)}h ago`
  return `${(h / 24).toFixed(1)}d ago`
}
